import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { LomavisDualSlider1 } from '../../../lomavisSharedUi/atoms/inputs/LomavisDualSlider1';
import { INDUSTRY_THRESHOLDS, SLIDER_CONFIG } from './constants';
import { updateLocationSettings, getLocationSettings } from './utils';
import { getCurrentLocationUUID } from '../../../utils/communicationUtils';
import toast from 'react-hot-toast';
import { useTranslate } from '@tolgee/react';

const AnalyticsSettingsPage: React.FC = () => {
  const { t } = useTranslate();
  const locationslug = getCurrentLocationUUID();
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [thresholdValues, setThresholdValues] = useState<[number, number]>(SLIDER_CONFIG.DEFAULT_VALUES);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Loading state for settings is tracked but not displayed in UI
  
  // Load settings on component mount
  React.useEffect(() => {
    const loadSettings = async () => {
      if (!locationslug) return;

      try {
        const settings = await getLocationSettings(locationslug);
        
        if (settings.post_performance_threshold_1 && settings.post_performance_threshold_2) {
          // Use values directly without conversion
          setThresholdValues([
            settings.post_performance_threshold_1,
            settings.post_performance_threshold_2
          ]);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        toast.error(t("lomavisSettings.toast.settingsLoadFailed"), {
          position: 'top-right',
        });
      }
    };

    void loadSettings();
  }, [locationslug, t]);

  const handleIndustryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const industry = event.target.value;
    setSelectedIndustry(industry);
    
    if (industry) {
      const selectedThreshold = INDUSTRY_THRESHOLDS.find(item => item.industry === industry);
      if (selectedThreshold) {
        // Use percentage values directly since they're already in percentage format
        setThresholdValues([
          selectedThreshold.post_performance_threshold_1,
          selectedThreshold.post_performance_threshold_2
        ]);
      }
    }
  };

  const handleSliderChange = (value: [number, number]) => {
    setThresholdValues(value);
    console.log('Threshold values changed:', value);
  };

  // Create a non-async wrapper for the save function to fix ESLint issue
  const handleSave = () => {
    const saveSettings = async () => {
      if (!locationslug) {
        toast.error(t('lomavisSettings.toast.locationNotFound'), {
          position: 'top-right',
        });
        return;
      }

      // Validate that threshold1 is less than threshold2
      if (thresholdValues[0] >= thresholdValues[1]) {
        toast.error(t('lomavisSettings.toast.thresholdValidationError', 'First threshold must be less than second threshold'), {
          position: 'top-right',
        });
        return;
      }
  
      setIsLoading(true);
      
      try {
        // Send values directly without conversion
        const settingsData = {
          post_performance_threshold_1: thresholdValues[0],
          post_performance_threshold_2: thresholdValues[1]
        };
  
        await updateLocationSettings(locationslug, settingsData);
        
        toast.success(t("lomavisSettings.toast.settingsSavedSuccessfully"), {
          position: 'top-right',
        });
      } catch (error) {
        console.error('Failed to save settings:', error);
        toast.error(t("lomavisSettings.toast.settingsSaveFailed"), {
          position: 'top-right',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    void saveSettings();
  };

  return (
    <div className="container-fluid p-4">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">{t("lomavisSettings.title.analyticsSettings")}</h5>
          
          <p className="text-muted mb-4">{t("lomavisSettings.description.analyticsSettings")}</p>

          <div className="d-flex justify-content-end mb-4">
            <div style={{ width: '300px' }}>
              <Form.Group>
                {/* Custom select implementation to fix translation issue */}
                <div className="form-select-container">
                  <select 
                    className="form-select form-select-sm"
                    value={selectedIndustry}
                    onChange={handleIndustryChange}
                    aria-label={t("lomavisSettings.aria.selectIndustry")}
                  >
                    <option value="">{t("lomavisSettings.placeholder.selectIndustry")}</option>
                    {INDUSTRY_THRESHOLDS.map((threshold, index) => (
                      <option key={index} value={threshold.industry}>
                        {t(`lomavisSettings.industry.${threshold.industry}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </Form.Group>
            </div>
          </div>
              <div className="mb-20"/>
          <div className="d-flex justify-content-center ">
            <LomavisDualSlider1 
              value={thresholdValues}
              onChange={handleSliderChange}
              min={SLIDER_CONFIG.MIN}
              max={SLIDER_CONFIG.MAX}
              step={SLIDER_CONFIG.STEP}
              label={t("lomavisSettings.label.engagementRate")}
              tooltipTextLeft={t("lomavisSettings.tooltip.leftThreshold")}
              tooltipTextRight={t("lomavisSettings.tooltip.rightThreshold")}
            />
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary"
                size="sm"
                onClick={() => window.location.reload()}
                className="me-2"
              >
                {t("buttons.cancel")}
              </Button>
              <Button 
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t("lomavisSettings.button.saving")}
                  </>
                ) : (
                  t("lomavisSettings.button.saveSettings")
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSettingsPage;